/**
 * Custom hooks per Schema-Attribute Junction
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
} from '@/lib/api-client';
import type {
  SchemaAttributeResponse,
  SchemaAttributeCreate,
  SchemaAttributeUpdate,
  SchemaAttributeListFilters,
} from '@/types/api/document-schemas';

const SCHEMA_ATTRIBUTES_QUERY_KEYS = {
  all: ['schema-attributes'] as const,
  lists: () => [...SCHEMA_ATTRIBUTES_QUERY_KEYS.all, 'list'] as const,
  list: (schemaId: string, filters?: SchemaAttributeListFilters) => [...SCHEMA_ATTRIBUTES_QUERY_KEYS.lists(), schemaId, filters] as const,
  details: () => [...SCHEMA_ATTRIBUTES_QUERY_KEYS.all, 'detail'] as const,
  detail: (schemaId: string, attrId: string) => [...SCHEMA_ATTRIBUTES_QUERY_KEYS.details(), schemaId, attrId] as const,
};

/**
 * Hook per lista attributes di uno schema
 */
export function useSchemaAttributes(schemaId: string, filters?: SchemaAttributeListFilters) {
  return useQuery({
    queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.list(schemaId, filters),
    queryFn: async (): Promise<SchemaAttributeResponse[]> => {
      return await apiGet<SchemaAttributeResponse[]>(
        `/document-schemas/${schemaId}/attributes`,
        { 
          params: filters as Record<string, string | number | boolean | null | undefined> | undefined 
        }
      );
    },
    enabled: !!schemaId,
  });
}

/**
 * Hook per aggiungere attribute a schema
 */
export function useAddAttributeToSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ schemaId, data }: { schemaId: string; data: SchemaAttributeCreate }): Promise<SchemaAttributeResponse> => {
      return await apiPost<SchemaAttributeResponse>(
        `/document-schemas/${schemaId}/attributes`,
        data
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.list(variables.schemaId) });
      queryClient.invalidateQueries({ queryKey: ['document-attributes'] });
      queryClient.invalidateQueries({ queryKey: ['document-schemas'] });
    },
  });
}

/**
 * Hook per singolo schema-attribute junction record
 */
export function useSchemaAttribute(schemaId: string, attrId: string) {
  return useQuery({
    queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.detail(schemaId, attrId),
    queryFn: async (): Promise<SchemaAttributeResponse> => {
      return await apiGet<SchemaAttributeResponse>(
        `/document-schemas/${schemaId}/attributes/${attrId}`
      );
    },
    enabled: !!schemaId && !!attrId,
  });
}

/**
 * Hook per aggiornare schema-attribute junction
 */
export function useUpdateSchemaAttribute() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ schemaId, attrId, data }: { schemaId: string; attrId: string; data: SchemaAttributeUpdate }): Promise<SchemaAttributeResponse> => {
      return await apiPut<SchemaAttributeResponse>(
        `/document-schemas/${schemaId}/attributes/${attrId}`,
        data
      );
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.list(variables.schemaId) });
      queryClient.invalidateQueries({ queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.detail(variables.schemaId, variables.attrId) });
    },
  });
}

/**
 * Hook per rimuovere attribute da schema
 */
export function useRemoveAttributeFromSchema() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ schemaId, attrId }: { schemaId: string; attrId: string }): Promise<void> => {
      return await apiDelete<void>(`/document-schemas/${schemaId}/attributes/${attrId}`);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: SCHEMA_ATTRIBUTES_QUERY_KEYS.list(variables.schemaId) });
      queryClient.invalidateQueries({ queryKey: ['document-attributes'] });
      queryClient.invalidateQueries({ queryKey: ['document-schemas'] });
    },
  });
}

